"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState, useRef } from "react";

interface NavMobileProps {
    navItems: { label: string; href: string }[];
}

function NavMobile({ navItems }: NavMobileProps) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [isMenuClosed, setIsMenuClosed] = useState(true);

    const pathname = usePathname();
    const menuRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!menuOpen) {
            const timer = setTimeout(() => {
                setIsMenuClosed(true);
            }, 200);
            return () => clearTimeout(timer);
        }
        setIsMenuClosed(false);
    }, [menuOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <>
            <button
                ref={buttonRef}
                className="sm:hidden text-white z-50 absolute left-4 top-1/2 -translate-y-1/2"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Abrir menu"
                aria-expanded={menuOpen ? "true" : "false"}
            >
                <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <title>Ícone de menu hambúrguer</title>
                    <desc>Ícone utilizado para abrir o menu de navegação</desc>
                    {isMenuClosed ? (
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 6h16M4 12h16M4 18h16"
                        />
                    ) : (
                        <></>
                    )}
                </svg>
            </button>

            {menuOpen && <div className="fixed inset-0 bg-black opacity-70 z-30 backdrop-blur-sm" />}

            <nav
                ref={menuRef}
                className={`fixed top-0 left-0 w-2/3 h-full bg-black text-white transform transition-transform duration-300 ${
                    menuOpen ? "translate-x-0" : "-translate-x-full"
                } sm:hidden z-40`}
                aria-label="Menu principal"
            >
                <div className="flex justify-end p-4">
                    <button onClick={() => setMenuOpen(false)} aria-label="Fechar menu">
                        <svg
                            className="w-6 h-6 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <title>Ícone de fechar</title>
                            <desc>Ícone utilizado para fechar o menu de navegação</desc>
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                <ul className="flex flex-col items-center gap-6 py-12">
                    {navItems.map(({ label, href }) => (
                        <li key={href}>
                            <Link
                                href={href}
                                className={`text-white tracking-wide uppercase ${
                                    pathname === href ? "font-bold" : "font-medium"
                                }`}
                                aria-current={pathname === href ? "page" : undefined}
                                onClick={() => setMenuOpen(false)}
                            >
                                {label}
                            </Link>
                        </li>
                    ))}
                </ul>
            </nav>
        </>
    );
}

export default NavMobile;
